import React, { useState, useEffect } from "react";
import "../assets/portal-player.css";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import SportsBasketballIcon from "@mui/icons-material/SportsBasketball";
import SchoolIcon from "@mui/icons-material/School";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ClassIcon from "@mui/icons-material/Class";
import EmailIcon from "@mui/icons-material/Email";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import HeightIcon from "@mui/icons-material/Height";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import axios from "axios";
import Loader from "./Utils/Loader";
import {
  X_factor_getCollegeColor,
  hasLoggedEnoughMinutes,
  DEFAULT_XFACTOR_LABEL,
} from "./Utils/functions";
import collegeConferences, {
  classXfactorMap,
} from "./Utils/collegeConferences";

const PortalPlayerModal = ({ playerId, onClose }) => {
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [activeStatsSeasonTab, setActiveStatsSeasonTab] = useState("24-25");
  const isMobile = window.innerWidth <= 768;

  // Helper function to get the appropriate data source based on selected season
  const getSeasonData = () => {
    if (!player) return null;

    if (activeStatsSeasonTab === "23-24" && player.season_2024) {
      return {
        ...player.season_2024,
        // Fallback to main player data for fields not in season_2024
        height: player.height,
        weight: player.weight,
        eligibility: player.eligibility,
        grad_transfer: player.grad_transfer,
        personal_contact: player.personal_contact,
        other_contact: player.other_contact,
        transferring_from: player.transferring_from || player.school,
        transferring_to: player.transferring_to || player.commitment,
        portal: player.portal,
        rank: player.season_2024.rank || player.rank,
        school: player.season_2024.current_team || player.school,
        current_team: player.season_2024.current_team || player.current_team,
        commitment: player.transferring_to || player.commitment,
      };
    }

    // Default to current season (24-25) data
    return player;
  };

  useEffect(() => {
    const fetchPlayerData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/portal/players/${playerId}`, {
          withCredentials: true,
        });
        setPlayer(response.data);
        if (isMobile && !response.data.portal) {
          setActiveTab("stats");
        }
        setLoading(false);
      } catch (err) {
        setError("Failed to load player data. Please try again later.");
        setLoading(false);
      }
    };
    if (playerId) fetchPlayerData();
  }, [playerId]);

  // Helper function to render player statistics view for a given season
  const renderPlayerStatsView = (
    statsSource,
    isPrevYearStats = false,
    mainPlayerData = null
  ) => {
    if (!statsSource) return <p>Statistics not available.</p>;
    const s = statsSource;
    const mpg = parseFloat(s.minutes_per_game) || 0;
    // Always use mainPlayerData (career data) for advanced metrics if provided
    const advancedSource = mainPlayerData || s;
    const getTrendArrow = (
      currentValue,
      previousValue,
      higherIsBetter = true
    ) => {
      if (isPrevYearStats || !player.season_2024) return null;
      const current = parseFloat(currentValue);
      const previous = parseFloat(previousValue);
      if (isNaN(current) || isNaN(previous) || previous === 0) return null;
      const percentChange = ((current - previous) / previous) * 100;
      if (Math.abs(percentChange) < 0.5) return null;
      const isIncreasing = current > previous;
      const isPositive = higherIsBetter ? isIncreasing : !isIncreasing;
      return isIncreasing ? (
        <ArrowUpwardRoundedIcon
          className={`portal-player-trend-arrow ${isPositive ? "up" : "down"}`}
          fontSize="small"
        />
      ) : (
        <ArrowDownwardRoundedIcon
          className={`portal-player-trend-arrow ${isPositive ? "up" : "down"}`}
          fontSize="small"
        />
      );
    };
    const getStatDisplay = (
      value,
      isPercentage = false,
      toFixed = 1,
      isRawValueForTBD = null
    ) => {
      const rawValue =
        isRawValueForTBD !== null
          ? parseFloat(isRawValueForTBD)
          : parseFloat(value);
      const displayValue = parseFloat(value);
      if (isNaN(displayValue))
        return isPrevYearStats && value === undefined ? "N/A" : "0.0";
      if (mpg > 5 && rawValue === 0) return "TBD";
      if (isPercentage) {
        return isNaN(displayValue)
          ? "0.0%"
          : (displayValue * 100).toFixed(toFixed) + "%";
      }
      return isNaN(displayValue) ? "0.0" : displayValue.toFixed(toFixed);
    };
    const ppg = isPrevYearStats
      ? parseFloat(s.pts) || 0
      : parseFloat(s.ppg) || 0;
    const rpg = isPrevYearStats
      ? parseFloat(s.rebs) || 0
      : parseFloat(s.rpg) || 0;
    const apg = isPrevYearStats
      ? parseFloat(s.asts) || 0
      : parseFloat(s.apg) || 0;
    const bpg = isPrevYearStats
      ? parseFloat(s.blks) || 0
      : parseFloat(s.blks) || 0;
    const spg = isPrevYearStats
      ? parseFloat(s.stls) || 0
      : parseFloat(s.stls) || 0;
    const topg = isPrevYearStats
      ? parseFloat(s.to) || 0
      : parseFloat(s.to) || 0;
    return (
      <div className="portal-player-stats-content-wrapper">
        <div className="portal-player-season-xfactor">
          <div className="portal-player-season-xfactor-label">X-Factor</div>
          <div
            className="portal-player-season-xfactor-value"
            style={{
              color: hasLoggedEnoughMinutes(s.games, s.minutes_per_game)
                ? X_factor_getCollegeColor(s.xfactor)
                : "#666",
            }}
          >
            {!hasLoggedEnoughMinutes(s.games, s.minutes_per_game)
              ? DEFAULT_XFACTOR_LABEL
              : s.xfactor || "N/A"}
            {hasLoggedEnoughMinutes(s.games, s.minutes_per_game) &&
              getTrendArrow(s.xfactor, player.season_2024?.xfactor)}
          </div>
        </div>
        <h4>Core Stats</h4>
        <div className="portal-player-stats-grid">
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(isPrevYearStats ? s.pts : s.ppg) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {getStatDisplay(ppg, false, 1, isPrevYearStats ? s.pts : s.ppg)}
              {getTrendArrow(s.ppg, player.season_2024?.pts)}
            </div>
            <div className="portal-player-stats-label">PPG</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(isPrevYearStats ? s.rebs : s.rpg) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {getStatDisplay(rpg, false, 1, isPrevYearStats ? s.rebs : s.rpg)}
              {getTrendArrow(s.rpg, player.season_2024?.rebs)}
            </div>
            <div className="portal-player-stats-label">RPG</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(isPrevYearStats ? s.asts : s.apg) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {getStatDisplay(apg, false, 1, isPrevYearStats ? s.asts : s.apg)}
              {getTrendArrow(s.apg, player.season_2024?.asts)}
            </div>
            <div className="portal-player-stats-label">APG</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(isPrevYearStats ? s.blks : s.blks) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {getStatDisplay(bpg, false, 1, isPrevYearStats ? s.blks : s.blks)}
              {getTrendArrow(s.blks, player.season_2024?.blks)}
            </div>
            <div className="portal-player-stats-label">BPG</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(isPrevYearStats ? s.stls : s.stls) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {getStatDisplay(spg, false, 1, isPrevYearStats ? s.stls : s.stls)}
              {getTrendArrow(s.stls, player.season_2024?.stls)}
            </div>
            <div className="portal-player-stats-label">SPG</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(isPrevYearStats ? s.to : s.to) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {getStatDisplay(topg, false, 1, isPrevYearStats ? s.to : s.to)}
              {getTrendArrow(s.to, player.season_2024?.to, false)}
            </div>
            <div className="portal-player-stats-label">TO</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(s.ts_pct) === 0 ? "tbd-value" : ""
              }`}
            >
              {getStatDisplay(s.ts_pct, true, 1, s.ts_pct)}
              {getTrendArrow(s.ts_pct, player.season_2024?.ts_pct)}
            </div>
            <div className="portal-player-stats-label">TS%</div>
          </div>
        </div>
        <h4>Playing Time</h4>
        <div className="portal-player-stats-grid">
          <div className="portal-player-stats-item">
            <div className="portal-player-stats-value">
              {s.games || "0"}
              {getTrendArrow(s.games, player.season_2024?.games)}
            </div>
            <div className="portal-player-stats-label">GP</div>
          </div>
          <div className="portal-player-stats-item">
            <div className="portal-player-stats-value">
              {getStatDisplay(s.minutes_per_game, false, 1, s.minutes_per_game)}
              {getTrendArrow(
                s.minutes_per_game,
                player.season_2024?.minutes_per_game
              )}
            </div>
            <div className="portal-player-stats-label">MPG</div>
          </div>
        </div>
        <h4>Advanced Metrics</h4>
        <div className="portal-player-stats-grid portal-player-advanced-section">
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.per) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.per
                ? "N/A"
                : getStatDisplay(
                    advancedSource.per,
                    false,
                    1,
                    advancedSource.per
                  )}
            </div>
            <div className="portal-player-stats-label">PER</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.pprod) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.pprod
                ? "N/A"
                : getStatDisplay(
                    advancedSource.pprod,
                    false,
                    1,
                    advancedSource.pprod
                  )}
            </div>
            <div className="portal-player-stats-label">PProd</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.orb_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.orb_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.orb_pct / 100,
                    true,
                    1,
                    advancedSource.orb_pct
                  )}
            </div>
            <div className="portal-player-stats-label">ORB%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.drb_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.drb_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.drb_pct / 100,
                    true,
                    1,
                    advancedSource.drb_pct
                  )}
            </div>
            <div className="portal-player-stats-label">DRB%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.efg_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.efg_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.efg_pct,
                    true,
                    1,
                    advancedSource.efg_pct
                  )}
            </div>
            <div className="portal-player-stats-label">eFG%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.three_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.three_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.three_pct,
                    true,
                    1,
                    advancedSource.three_pct
                  )}
            </div>
            <div className="portal-player-stats-label">3P%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.ast_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.ast_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.ast_pct / 100,
                    true,
                    1,
                    advancedSource.ast_pct
                  )}
            </div>
            <div className="portal-player-stats-label">AST%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.stl_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.stl_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.stl_pct / 100,
                    true,
                    1,
                    advancedSource.stl_pct
                  )}
            </div>
            <div className="portal-player-stats-label">STL%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.blk_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.blk_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.blk_pct / 100,
                    true,
                    1,
                    advancedSource.blk_pct
                  )}
            </div>
            <div className="portal-player-stats-label">BLK%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.tov_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.tov_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.tov_pct / 100,
                    true,
                    1,
                    advancedSource.tov_pct
                  )}
            </div>
            <div className="portal-player-stats-label">TOV%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.usg_pct) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.usg_pct
                ? "N/A"
                : getStatDisplay(
                    advancedSource.usg_pct / 100,
                    true,
                    1,
                    advancedSource.usg_pct
                  )}
            </div>
            <div className="portal-player-stats-label">USG%</div>
          </div>
          <div className="portal-player-stats-item">
            <div
              className={`portal-player-stats-value ${
                mpg > 5 && parseFloat(advancedSource.ftr) === 0
                  ? "tbd-value"
                  : ""
              }`}
            >
              {!advancedSource.ftr
                ? "N/A"
                : getStatDisplay(
                    advancedSource.ftr,
                    true,
                    1,
                    advancedSource.ftr
                  )}
            </div>
            <div className="portal-player-stats-label">FTR</div>
          </div>
        </div>
      </div>
    );
  };

  const renderClassXfactorLegend = () => {
    const yearKey = activeStatsSeasonTab === "23-24" ? 2024 : 2025;
    const map = classXfactorMap[yearKey];
    if (!map) return null;

    const classesOrder = ["fr", "so", "jr", "sr"];

    return (
      <div className="ncaa-class-legend">
        {classesOrder.map((cls) =>
          map[cls] ? (
            <div key={cls} className="ncaa-class-legend-item">
              <span className="ncaa-class-legend-label">
                {cls.toUpperCase()}
              </span>
              <span
                className="ncaa-class-legend-value"
                style={{ color: X_factor_getCollegeColor(map[cls]) }}
              >
                {map[cls].toFixed(1)}
              </span>
            </div>
          ) : null
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="portal-player-view-overlay">
        <div className="portal-player-view-modal">
          <div className="transfer-portal-loading">
            <Loader />
            <p>Loading player data...</p>
          </div>
        </div>
      </div>
    );
  }
  if (error || !player) {
    return (
      <div className="portal-player-view-overlay">
        <div className="portal-player-view-modal">
          <div className="transfer-portal-error">
            <p>{error || "Player not found."}</p>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const seasonData = getSeasonData();
  const notEnoughMinutes = seasonData
    ? !hasLoggedEnoughMinutes(seasonData.games, seasonData.minutes_per_game)
    : false;

  return (
    <div className="portal-player-view-overlay" onClick={onClose}>
      <div
        className="portal-player-view-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="portal-player-header"
          style={{
            borderBottom: "1px solid #4a5568",
            background: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div className="portal-player-title">
            <h1>Player Details</h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              className="portal-player-view-new-tab"
              onClick={() => window.open(`/ncaa/${playerId}`, "_blank")}
              title="Open in New Tab"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "8px",
                borderRadius: "50%",
              }}
            >
              <LaunchIcon />
            </button>
            <button className="portal-player-view-close" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="portal-player-content">
          {isMobile ? (
            <div className="portal-player-mobile-view">
              {/* --- MOBILE LAYOUT --- */}
              {/* Copy the mobile layout from PortalPlayer.jsx here, including tabs and sections */}
              {/* ... (see PortalPlayer.jsx for full structure) ... */}
              <div className="portal-player-profile">
                <div className="portal-player-image-container">
                  <img
                    src={
                      player.image ||
                      "https://d3mvy6uwazuxyu.cloudfront.net/back-b-2.png"
                    }
                    alt={player.name}
                    className="portal-player-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      const backupImages = [
                        "https://d3mvy6uwazuxyu.cloudfront.net/back-b-2.png",
                        "https://d3mvy6uwazuxyu.cloudfront.net/back-b-3.png",
                        "https://d3mvy6uwazuxyu.cloudfront.net/back-b-4.png",
                      ];
                      const randomBackup =
                        backupImages[
                          Math.floor(Math.random() * backupImages.length)
                        ];
                      e.target.src = randomBackup;
                    }}
                  />
                </div>
                <div className="portal-player-info">
                  <h2 className="portal-player-name">{player.name}</h2>
                  <div className="portal-player-position-row">
                    <div className="portal-player-xfactor">
                      <span>X-Factor</span>
                      <div
                        className="portal-player-xfactor-value"
                        style={{
                          color: notEnoughMinutes
                            ? "#666"
                            : X_factor_getCollegeColor(
                                getSeasonData()?.xfactor
                              ),
                        }}
                      >
                        {notEnoughMinutes
                          ? DEFAULT_XFACTOR_LABEL
                          : getSeasonData()?.xfactor}
                      </div>
                    </div>
                    <div className="portal-player-info-item">
                      <span className="portal-player-info-label">Position</span>
                      <span className="portal-player-position">
                        {getSeasonData()?.position}
                      </span>
                    </div>
                    <div className="portal-player-info-item">
                      <span className="portal-player-info-label">Class</span>
                      <span className="portal-player-class">
                        {getSeasonData()?.class}
                      </span>
                    </div>
                  </div>
                  {notEnoughMinutes && (
                    <div className="transfer-portal-minutes-note">
                      * Didn't play much:{" "}
                      {Math.round(
                        getSeasonData()?.games *
                          getSeasonData()?.minutes_per_game
                      )}{" "}
                      total mins logged
                    </div>
                  )}
                  <div className="portal-player-legend-container">
                    <div className="portal-player-legend">
                      <ul>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(145),
                            }}
                          ></span>{" "}
                          Impact (145+)
                        </li>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(130),
                            }}
                          ></span>{" "}
                          Starter (130-144)
                        </li>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(115),
                            }}
                          ></span>{" "}
                          Rotational (115-129)
                        </li>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(105),
                            }}
                          ></span>{" "}
                          Role Player (105-114)
                        </li>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(104),
                            }}
                          ></span>{" "}
                          Developmental (under 105)
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="portal-player-conference-info">
                    <div className="portal-player-detail-item">
                      <span className="portal-player-detail-label">
                        <SchoolIcon
                          fontSize="small"
                          style={{ marginRight: "4px" }}
                        />
                        School
                      </span>
                      <span className="portal-player-detail-value">
                        {getSeasonData()?.current_team ||
                          getSeasonData()?.school ||
                          "N/A"}
                      </span>
                    </div>
                    <div className="portal-player-detail-item">
                      <span className="portal-player-detail-label">
                        <EmojiEventsIcon
                          fontSize="small"
                          style={{ marginRight: "4px" }}
                        />
                        Conference
                      </span>
                      <span className="portal-player-detail-value">
                        {getSeasonData()?.conf}
                      </span>
                    </div>
                    {getSeasonData()?.team_rating && (
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          Team X-Factor
                        </span>
                        <span
                          className="portal-player-detail-value"
                          style={{
                            color: X_factor_getCollegeColor(
                              getSeasonData()?.team_rating
                            ),
                            fontWeight: "bold",
                          }}
                        >
                          {getSeasonData()?.team_rating}
                        </span>
                      </div>
                    )}
                    {collegeConferences[getSeasonData()?.conf] && (
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          Conference X-Factor
                        </span>
                        <span
                          className="portal-player-detail-value"
                          style={{
                            color: X_factor_getCollegeColor(
                              collegeConferences[getSeasonData()?.conf].xfactor
                            ),
                            fontWeight: "bold",
                          }}
                        >
                          {collegeConferences[getSeasonData()?.conf].xfactor}
                        </span>
                      </div>
                    )}
                    <div className="portal-player-detail-item">
                      <span className="portal-player-detail-label">
                        NCAA Class X-Factor
                      </span>
                      <span
                        className="portal-player-detail-value"
                        style={{
                          color: X_factor_getCollegeColor(
                            getSeasonData()?.class &&
                              classXfactorMap[
                                activeStatsSeasonTab === "23-24" ? 2024 : 2025
                              ] &&
                              classXfactorMap[
                                activeStatsSeasonTab === "23-24" ? 2024 : 2025
                              ][getSeasonData()?.class.toLowerCase()]
                              ? classXfactorMap[
                                  activeStatsSeasonTab === "23-24" ? 2024 : 2025
                                ][getSeasonData()?.class.toLowerCase()]
                              : 0
                          ),
                          fontWeight: "bold",
                        }}
                      >
                        {getSeasonData()?.class &&
                        classXfactorMap[
                          activeStatsSeasonTab === "23-24" ? 2024 : 2025
                        ] &&
                        classXfactorMap[
                          activeStatsSeasonTab === "23-24" ? 2024 : 2025
                        ][getSeasonData()?.class.toLowerCase()]
                          ? classXfactorMap[
                              activeStatsSeasonTab === "23-24" ? 2024 : 2025
                            ][getSeasonData()?.class.toLowerCase()].toFixed(1)
                          : "0"}
                      </span>
                    </div>
                  </div>
                  {renderClassXfactorLegend()}
                  {player.portal && (
                    <>
                      <div className="portal-player-transfer-status-container">
                        <div className="portal-player-schools">
                          <div className="portal-player-school from">
                            <SchoolIcon />
                            <span>
                              {player.transferring_from || player.school}
                            </span>
                          </div>
                          <ArrowForwardIcon className="portal-player-arrow" />
                          <div className="portal-player-school to">
                            {player.commitment || player.transferring_to ? (
                              <>
                                <SchoolIcon />
                                <span>
                                  {player.commitment || player.transferring_to}
                                </span>
                              </>
                            ) : (
                              <span className="portal-player-undecided">
                                Undecided
                              </span>
                            )}
                          </div>
                        </div>
                        <div
                          className={`portal-player-transfer-status ${
                            player.commitment || player.transferring_to
                              ? "committed"
                              : "available"
                          }`}
                        >
                          {player.commitment || player.transferring_to
                            ? "Committed"
                            : "Available"}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="portal-player-mobile-tabs">
                <div className="portal-player-tab-buttons">
                  {player.portal && (
                    <button
                      className={`portal-player-tab-button ${
                        activeTab === "basic" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("basic")}
                    >
                      Basic Info
                    </button>
                  )}
                  <button
                    className={`portal-player-tab-button ${
                      activeTab === "stats" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("stats")}
                  >
                    Season Stats
                  </button>
                  {player.portal && (
                    <button
                      className={`portal-player-tab-button ${
                        activeTab === "contact" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("contact")}
                    >
                      Contact
                    </button>
                  )}
                </div>
                {activeTab === "basic" && player.portal && (
                  <div className="portal-player-details">
                    <div className="portal-player-detail-row">
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          <HeightIcon
                            fontSize="small"
                            style={{ marginRight: "4px" }}
                          />
                          Height
                        </span>
                        <span className="portal-player-detail-value">
                          {player.height || "N/A"}
                        </span>
                      </div>
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          <FitnessCenterIcon
                            fontSize="small"
                            style={{ marginRight: "4px" }}
                          />
                          Weight
                        </span>
                        <span className="portal-player-detail-value">
                          {player.weight ? `${player.weight} lbs` : "N/A"}
                        </span>
                      </div>
                    </div>
                    <div className="portal-player-detail-row">
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          Years Remaining
                        </span>
                        <span className="portal-player-detail-value">
                          {player.eligibility || "N/A"}
                        </span>
                      </div>
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          Grad Transfer
                        </span>
                        <span className="portal-player-detail-value">
                          {player.grad_transfer ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {activeTab === "stats" && (
                  <div className="portal-player-stats-container">
                    <div className="portal-player-season-tabs">
                      <button
                        className={`portal-player-season-tab-button ${
                          activeStatsSeasonTab === "24-25" ? "active" : ""
                        }`}
                        onClick={() => setActiveStatsSeasonTab("24-25")}
                      >
                        2024-25 Stats
                      </button>
                      {player.season_2024 && (
                        <button
                          className={`portal-player-season-tab-button ${
                            activeStatsSeasonTab === "23-24" ? "active" : ""
                          }`}
                          onClick={() => setActiveStatsSeasonTab("23-24")}
                        >
                          2023-24 Stats
                        </button>
                      )}
                    </div>
                    {activeStatsSeasonTab === "24-25" &&
                      renderPlayerStatsView(player, false)}
                    {activeStatsSeasonTab === "23-24" &&
                      player.season_2024 &&
                      renderPlayerStatsView(player.season_2024, true, player)}
                  </div>
                )}
                {activeTab === "contact" && player.portal && (
                  <div className="portal-player-contact">
                    <h3 className="portal-player-contact-title">
                      Contact Information
                    </h3>
                    <div className="portal-player-contact-item">
                      <EmailIcon className="portal-player-contact-icon" />
                      <div className="portal-player-contact-text">
                        {player.personal_contact ||
                          "No personal contact information available"}
                      </div>
                    </div>
                    <div className="portal-player-contact-item">
                      <ContactPhoneIcon className="portal-player-contact-icon" />
                      <div className="portal-player-contact-text">
                        {player.other_contact ||
                          "No other contact information available"}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // DESKTOP LAYOUT
            <div className="portal-player-profile-row">
              <div className="portal-player-profile">
                <div className="portal-player-image-container">
                  <img
                    src={
                      player.image ||
                      "https://d3mvy6uwazuxyu.cloudfront.net/back-b-2.png"
                    }
                    alt={player.name}
                    className="portal-player-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      const backupImages = [
                        "https://d3mvy6uwazuxyu.cloudfront.net/back-b-2.png",
                        "https://d3mvy6uwazuxyu.cloudfront.net/back-b-3.png",
                        "https://d3mvy6uwazuxyu.cloudfront.net/back-b-4.png",
                      ];
                      const randomBackup =
                        backupImages[
                          Math.floor(Math.random() * backupImages.length)
                        ];
                      e.target.src = randomBackup;
                    }}
                  />
                </div>
                <div className="portal-player-info">
                  <h2 className="portal-player-name">{player.name}</h2>
                  <div className="portal-player-position-row">
                    <div className="portal-player-xfactor">
                      <span>X-Factor</span>
                      <div
                        className="portal-player-xfactor-value"
                        style={{
                          color: notEnoughMinutes
                            ? "#666"
                            : X_factor_getCollegeColor(
                                getSeasonData()?.xfactor
                              ),
                        }}
                      >
                        {notEnoughMinutes
                          ? DEFAULT_XFACTOR_LABEL
                          : getSeasonData()?.xfactor}
                      </div>
                    </div>
                    <div className="portal-player-info-item">
                      <span className="portal-player-info-label">Position</span>
                      <span className="portal-player-position">
                        {getSeasonData()?.position}
                      </span>
                    </div>
                    <div className="portal-player-info-item">
                      <span className="portal-player-info-label">Class</span>
                      <span className="portal-player-class">
                        {getSeasonData()?.class}
                      </span>
                    </div>
                  </div>
                  {notEnoughMinutes && (
                    <div className="transfer-portal-minutes-note">
                      * Didn't play much:{" "}
                      {Math.round(
                        getSeasonData()?.games *
                          getSeasonData()?.minutes_per_game
                      )}{" "}
                      total mins logged
                    </div>
                  )}
                  <div className="portal-player-legend-container">
                    <div className="portal-player-legend">
                      <ul>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(145),
                            }}
                          ></span>{" "}
                          Impact (145+)
                        </li>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(130),
                            }}
                          ></span>{" "}
                          Starter (130-144)
                        </li>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(115),
                            }}
                          ></span>{" "}
                          Rotational (115-129)
                        </li>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(105),
                            }}
                          ></span>{" "}
                          Role Player (105-114)
                        </li>
                        <li>
                          <span
                            style={{
                              backgroundColor: X_factor_getCollegeColor(104),
                            }}
                          ></span>{" "}
                          Developmental (under 105)
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="portal-player-conference-info">
                    <div className="portal-player-detail-item">
                      <span className="portal-player-detail-label">
                        <SchoolIcon
                          fontSize="small"
                          style={{ marginRight: "4px" }}
                        />
                        School
                      </span>
                      <span className="portal-player-detail-value">
                        {getSeasonData()?.current_team ||
                          getSeasonData()?.school ||
                          "N/A"}
                      </span>
                    </div>
                    <div className="portal-player-detail-item">
                      <span className="portal-player-detail-label">
                        <EmojiEventsIcon
                          fontSize="small"
                          style={{ marginRight: "4px" }}
                        />
                        Conference
                      </span>
                      <span className="portal-player-detail-value">
                        {getSeasonData()?.conf}
                      </span>
                    </div>
                    {getSeasonData()?.team_rating && (
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          Team X-Factor
                        </span>
                        <span
                          className="portal-player-detail-value"
                          style={{
                            color: X_factor_getCollegeColor(
                              getSeasonData()?.team_rating
                            ),
                            fontWeight: "bold",
                          }}
                        >
                          {getSeasonData()?.team_rating}
                        </span>
                      </div>
                    )}
                    {collegeConferences[getSeasonData()?.conf] && (
                      <div className="portal-player-detail-item">
                        <span className="portal-player-detail-label">
                          Conference X-Factor
                        </span>
                        <span
                          className="portal-player-detail-value"
                          style={{
                            color: X_factor_getCollegeColor(
                              collegeConferences[getSeasonData()?.conf].xfactor
                            ),
                            fontWeight: "bold",
                          }}
                        >
                          {collegeConferences[getSeasonData()?.conf].xfactor}
                        </span>
                      </div>
                    )}
                    <div className="portal-player-detail-item">
                      <span className="portal-player-detail-label">
                        NCAA Class X-Factor
                      </span>
                      <span
                        className="portal-player-detail-value"
                        style={{
                          color: X_factor_getCollegeColor(
                            getSeasonData()?.class &&
                              classXfactorMap[
                                activeStatsSeasonTab === "23-24" ? 2024 : 2025
                              ] &&
                              classXfactorMap[
                                activeStatsSeasonTab === "23-24" ? 2024 : 2025
                              ][getSeasonData()?.class.toLowerCase()]
                              ? classXfactorMap[
                                  activeStatsSeasonTab === "23-24" ? 2024 : 2025
                                ][getSeasonData()?.class.toLowerCase()]
                              : 0
                          ),
                          fontWeight: "bold",
                        }}
                      >
                        {getSeasonData()?.class &&
                        classXfactorMap[
                          activeStatsSeasonTab === "23-24" ? 2024 : 2025
                        ] &&
                        classXfactorMap[
                          activeStatsSeasonTab === "23-24" ? 2024 : 2025
                        ][getSeasonData()?.class.toLowerCase()]
                          ? classXfactorMap[
                              activeStatsSeasonTab === "23-24" ? 2024 : 2025
                            ][getSeasonData()?.class.toLowerCase()].toFixed(1)
                          : "0"}
                      </span>
                    </div>
                    {renderClassXfactorLegend()}
                  </div>

                  {player.portal && (
                    <>
                      <div className="portal-player-transfer-status-container">
                        <div className="portal-player-schools">
                          <div className="portal-player-school from">
                            <SchoolIcon />
                            <span>
                              {player.transferring_from || player.school}
                            </span>
                          </div>
                          <ArrowForwardIcon className="portal-player-arrow" />
                          <div className="portal-player-school to">
                            {player.commitment || player.transferring_to ? (
                              <>
                                <SchoolIcon />
                                <span>
                                  {player.commitment || player.transferring_to}
                                </span>
                              </>
                            ) : (
                              <span className="portal-player-undecided">
                                Undecided
                              </span>
                            )}
                          </div>
                        </div>
                        <div
                          className={`portal-player-transfer-status ${
                            player.commitment || player.transferring_to
                              ? "committed"
                              : "available"
                          }`}
                        >
                          {player.commitment || player.transferring_to
                            ? "Committed"
                            : "Available"}
                        </div>
                      </div>
                      <div className="portal-player-details">
                        <div className="portal-player-detail-row">
                          <div className="portal-player-detail-item">
                            <span className="portal-player-detail-label">
                              <HeightIcon
                                fontSize="small"
                                style={{ marginRight: "4px" }}
                              />
                              Height
                            </span>
                            <span className="portal-player-detail-value">
                              {player.height || "N/A"}
                            </span>
                          </div>
                          <div className="portal-player-detail-item">
                            <span className="portal-player-detail-label">
                              <FitnessCenterIcon
                                fontSize="small"
                                style={{ marginRight: "4px" }}
                              />
                              Weight
                            </span>
                            <span className="portal-player-detail-value">
                              {player.weight ? `${player.weight} lbs` : "N/A"}
                            </span>
                          </div>
                        </div>
                        <div className="portal-player-detail-row">
                          <div className="portal-player-detail-item">
                            <span className="portal-player-detail-label">
                              Years Remaining
                            </span>
                            <span className="portal-player-detail-value">
                              {player.eligibility || "N/A"}
                            </span>
                          </div>
                          <div className="portal-player-detail-item">
                            <span className="portal-player-detail-label">
                              Grad Transfer
                            </span>
                            <span className="portal-player-detail-value">
                              {player.grad_transfer ? "Yes" : "No"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="portal-player-contact">
                        <h3 className="portal-player-contact-title">
                          Contact Information
                        </h3>
                        <div className="portal-player-contact-item">
                          <EmailIcon className="portal-player-contact-icon" />
                          <div className="portal-player-contact-text">
                            {player.personal_contact ||
                              "No personal contact information available"}
                          </div>
                        </div>
                        <div className="portal-player-contact-item">
                          <ContactPhoneIcon className="portal-player-contact-icon" />
                          <div className="portal-player-contact-text">
                            {player.other_contact ||
                              "No other contact information available"}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="portal-player-stats-card">
                <h3 className="portal-player-card-title">
                  <AssessmentIcon style={{ marginRight: "8px" }} />
                  Player Stats
                </h3>
                <div className="portal-player-season-tabs">
                  <button
                    className={`portal-player-season-tab-button ${
                      activeStatsSeasonTab === "24-25" ? "active" : ""
                    }`}
                    onClick={() => setActiveStatsSeasonTab("24-25")}
                  >
                    2024-25
                  </button>
                  {player.season_2024 && (
                    <button
                      className={`portal-player-season-tab-button ${
                        activeStatsSeasonTab === "23-24" ? "active" : ""
                      }`}
                      onClick={() => setActiveStatsSeasonTab("23-24")}
                    >
                      2023-24
                    </button>
                  )}
                </div>
                {activeStatsSeasonTab === "24-25" &&
                  renderPlayerStatsView(player, false)}
                {activeStatsSeasonTab === "23-24" &&
                  player.season_2024 &&
                  renderPlayerStatsView(player.season_2024, true, player)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalPlayerModal;
